// Admin API Keys & Integrations Management Page
// ─────────────────────────────────────────────────────────────
// Full UI for managing all third-party API keys and service
// configurations from the admin panel.

import React, { useState, useEffect } from 'react';
import {
  Key,
  Bell,
  Mail,
  MessageSquare,
  Truck,
  CreditCard,
  Search,
  Shield,
  Check,
  X,
  Loader2,
  ExternalLink,
  Eye,
  EyeOff,
  RefreshCcw,
  Save,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Zap,
  ChevronDown,
  Copy,
  Info,
  Megaphone,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import {
  useAdminConfig,
  INTEGRATIONS,
  type IntegrationMeta,
  type IntegrationSection,
  type IntegrationField,
} from '../../hooks/useAdminConfig';

// ── Icon mapper ─────────────────────────────────────────────

const iconMap: Record<string, React.ElementType> = {
  Bell,
  Mail,
  MessageSquare,
  Megaphone,
  Truck,
  CreditCard,
  Search,
};

const colorMap: Record<string, { bg: string; text: string; ring: string; badge: string }> = {
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-200', badge: 'bg-amber-100 text-amber-700' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-200', badge: 'bg-blue-100 text-blue-700' },
  red: { bg: 'bg-red-50', text: 'text-red-600', ring: 'ring-red-200', badge: 'bg-red-100 text-red-700' },
  green: { bg: 'bg-green-50', text: 'text-green-600', ring: 'ring-green-200', badge: 'bg-green-100 text-green-700' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600', ring: 'ring-gray-200', badge: 'bg-gray-200 text-gray-700' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', ring: 'ring-purple-200', badge: 'bg-purple-100 text-purple-700' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', ring: 'ring-indigo-200', badge: 'bg-indigo-100 text-indigo-700' },
};

// ── Secret Input Component ──────────────────────────────────

function SecretInput({
  field,
  value,
  onChange,
  isSet,
}: {
  field: IntegrationField;
  value: string;
  onChange: (val: string) => void;
  isSet?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const isPassword = field.type === 'password';
  const isMasked = value.startsWith('••••');

  const handleCopy = () => {
    if (!isMasked) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="flex items-center justify-between">
        <span className="text-sm font-medium text-heading">
          {field.label}
          {field.required && <span className="text-red-400 ml-0.5">*</span>}
        </span>
        {isSet && isMasked && (
          <span className="text-xs text-green-600 font-medium flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Configured
          </span>
        )}
      </label>
      <div className="relative">
        <Input
          type={isPassword && !visible ? 'password' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isMasked ? 'Enter new value to update' : field.placeholder}
          className={cn(
            'pr-20 h-10 text-sm font-mono',
            isMasked && 'text-gray-400 italic'
          )}
          onFocus={() => {
            // Clear masked value on focus so user can type new one
            if (isMasked) {
              onChange('');
            }
          }}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isPassword && (
            <button
              type="button"
              onClick={() => setVisible(!visible)}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title={visible ? 'Hide' : 'Show'}
            >
              {visible ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          )}
          {value && !isMasked && (
            <button
              type="button"
              onClick={handleCopy}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title="Copy"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 text-gray-400" />
              )}
            </button>
          )}
        </div>
      </div>
      {field.helpText && (
        <p className="text-xs text-body flex items-start gap-1.5 mt-1">
          <Info className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          {field.helpText}
        </p>
      )}
    </div>
  );
}

// ── Integration Card ────────────────────────────────────────

function IntegrationCard({
  integration,
  config,
  status,
  saving,
  onSave,
  onTest,
}: {
  integration: IntegrationMeta;
  config: Record<string, any>;
  status: { configured: boolean; testing: boolean; testResult?: { success: boolean; message: string } };
  saving: boolean;
  onSave: (config: Record<string, any>) => void;
  onTest: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const Icon = iconMap[integration.icon] || Key;
  const colors = colorMap[integration.color] || colorMap.gray;

  // Initialize local values from config
  useEffect(() => {
    const vals: Record<string, string> = {};
    for (const field of integration.fields) {
      vals[field.key] = config[field.key] || '';
    }
    setLocalValues(vals);
    setHasChanges(false);
  }, [config, integration.fields]);

  const handleFieldChange = (key: string, value: string) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(localValues);
    setHasChanges(false);
  };

  const handleDiscard = () => {
    const vals: Record<string, string> = {};
    for (const field of integration.fields) {
      vals[field.key] = config[field.key] || '';
    }
    setLocalValues(vals);
    setHasChanges(false);
  };

  return (
    <Card className={cn(
      'overflow-hidden transition-all duration-200',
      expanded && 'ring-2',
      expanded && colors.ring,
    )}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center space-x-4">
          <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', colors.bg)}>
            <Icon className={cn('h-6 w-6', colors.text)} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-heading">{integration.name}</h3>
              {status.configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <CheckCircle2 className="h-3 w-3" />
                  Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  Not configured
                </span>
              )}
            </div>
            <p className="text-sm text-body mt-0.5">{integration.description}</p>
          </div>
        </div>
        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 ml-4"
        >
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </motion.div>
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 sm:px-5 pb-5 border-t border-gray-100 pt-5">
              {/* Fields */}
              <div className="space-y-4">
                {integration.fields.map((field) => (
                  <SecretInput
                    key={field.key}
                    field={field}
                    value={localValues[field.key] || ''}
                    onChange={(val) => handleFieldChange(field.key, val)}
                    isSet={config[`${field.key}_set`]}
                  />
                ))}
              </div>

              {/* Test Result */}
              <AnimatePresence>
                {status.testResult && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={cn(
                      'mt-4 p-3 rounded-lg flex items-start gap-2',
                      status.testResult.success
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    )}
                  >
                    {status.testResult.success ? (
                      <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 flex-shrink-0" />
                    )}
                    <span className="text-sm">{status.testResult.message}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <a
                    href={integration.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Console
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  {status.configured && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onTest}
                      disabled={status.testing}
                      className="h-9"
                    >
                      {status.testing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Zap className="h-4 w-4 mr-1.5" />
                      )}
                      Test
                    </Button>
                  )}
                  {hasChanges && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDiscard}
                        className="h-9"
                      >
                        Discard
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                        className="h-9"
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                        ) : (
                          <Save className="h-4 w-4 mr-1.5" />
                        )}
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════

export function AdminApiKeys() {
  const {
    config,
    loading,
    error,
    saving,
    statuses,
    saveSection,
    testIntegration,
    reload,
  } = useAdminConfig();

  const configuredCount = Object.values(statuses).filter((s) => s.configured).length;
  const totalCount = INTEGRATIONS.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-3 text-body">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">API Keys & Integrations</h1>
          <p className="text-body text-sm mt-1">
            Manage your third-party service credentials and API keys
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-body">
            <span className="font-semibold text-heading">{configuredCount}</span> of {totalCount} configured
          </span>
          <Button variant="outline" size="sm" onClick={reload} className="h-9">
            <RefreshCcw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-0.5">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {INTEGRATIONS.map((integration) => {
          const Icon = iconMap[integration.icon] || Key;
          const colors = colorMap[integration.color] || colorMap.gray;
          const isConfigured = statuses[integration.id]?.configured;

          return (
            <Card
              key={integration.id}
              className={cn(
                'transition-all',
                isConfigured ? 'border-green-200' : 'border-gray-200'
              )}
            >
              <CardContent className="p-3 text-center">
                <div className={cn(
                  'w-10 h-10 rounded-lg mx-auto flex items-center justify-center mb-2',
                  isConfigured ? 'bg-green-50' : colors.bg
                )}>
                  {isConfigured ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <Icon className={cn('h-5 w-5', colors.text)} />
                  )}
                </div>
                <p className="text-xs font-medium text-heading truncate">{integration.name}</p>
                <p className={cn(
                  'text-[10px] font-medium mt-1',
                  isConfigured ? 'text-green-600' : 'text-gray-400'
                )}>
                  {isConfigured ? 'Active' : 'Not set'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Security Notice */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900 text-sm">Security Notice</p>
            <p className="text-xs text-amber-700 mt-1">
              API keys are encrypted and stored securely in Firestore. Keys are masked after saving
              and only the last 4 characters are displayed. Only admin users can access this page.
              Never share your API keys with anyone.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Integration Cards */}
      <div className="space-y-3">
        {INTEGRATIONS.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            config={(config as any)[integration.id] || {}}
            status={statuses[integration.id] || { configured: false, testing: false }}
            saving={saving === integration.id}
            onSave={(sectionConfig) => saveSection(integration.id, sectionConfig)}
            onTest={() => testIntegration(integration.id)}
          />
        ))}
      </div>

      {/* Footer Help */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-heading mb-3">Quick Setup Guide</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-heading">Required for Launch</h4>
              <ul className="space-y-2 text-sm text-body">
                <li className="flex items-center gap-2">
                  {statuses.firebase_messaging?.configured ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  )}
                  Firebase Messaging (push notifications)
                </li>
                <li className="flex items-center gap-2">
                  {statuses.sendgrid?.configured ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  )}
                  SendGrid (email receipts & alerts)
                </li>
                <li className="flex items-center gap-2">
                  {statuses.stripe_platform?.configured ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  )}
                  Stripe (payment processing)
                </li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-heading">Optional / Enhanced</h4>
              <ul className="space-y-2 text-sm text-body">
                <li className="flex items-center gap-2">
                  {statuses.uber_direct?.configured ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  )}
                  Uber Direct (courier delivery)
                </li>
                <li className="flex items-center gap-2">
                  {statuses.twilio?.configured ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  )}
                  Twilio (SMS notifications)
                </li>
                <li className="flex items-center gap-2">
                  {statuses.algolia?.configured ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  )}
                  Algolia (product search)
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
